import { useCallback, useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";

const newSession = () => `chat-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

/** Streams answers about one site's figures and keeps the transcript for the session. */
export function useAiChat({ siteId, start, days }) {
  const [sessionId] = useState(newSession);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);

  useEffect(() => {
    api.get(`/ai/chat/${sessionId}`).then(({ data }) => setMessages(data.messages || [])).catch(() => {});
  }, [sessionId]);

  const replaceLast = (text) =>
    setMessages((m) => {
      const next = [...m];
      next[next.length - 1] = { role: "assistant", text };
      return next;
    });

  const ask = useCallback(async (text) => {
    const q = (text || "").trim();
    if (!q || streaming) return;
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API_BASE}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ site_id: siteId, session_id: sessionId, question: q, start, days }),
      });
      if (!res.ok || !res.body) throw new Error(`Request failed (${res.status})`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { role: "assistant", text: next[next.length - 1].text + chunk };
          return next;
        });
      }
    } catch (e) {
      replaceLast(`Sorry — ${e.message}`);
    } finally {
      setStreaming(false);
    }
  }, [siteId, sessionId, start, days, streaming]);

  return { messages, streaming, ask };
}
